import { RING_COLORS } from "../maps/tokens";
import { buildDieSvg, type DieFx, type DieSvgOptions } from "./die_svg";
import { LANES, THROW_MS, THROW_TOUCHES, type ThrownDie, type ThrowSchedule, throwMoments } from "./landing";

export type Face = [sides: number, value: number];
export type Point = { x: number; y: number };
// A rect in the layer's own pixels.
export type Box = { left: number; top: number; right: number; bottom: number };

const SM_PX = 640;
const EDGE_PX = 24;
const SPIN_TURNS_DEG = 700;

// `scale` is the zoom the view draws its roll cards at (the table view's 1.5):
// the dice, their gaps, the lanes and the margins all grow by it.
export type ThrowArea = { width: number; height: number; box?: Box | null; bottomInset?: number; scale?: number };

// Where each die of a lane settles, its top-left in the layer, or null when the
// board has no room for it. The dice land in a row right of the roller's tab, the lanes
// stacked above the bottom edge; on a desktop an open dice box pushes the row
// to its right, and on a phone, where the box and the sheets take the bottom,
// the lanes rise above them and the card stack. A short board (a phone held
// sideways) lowers them and narrows their step, so the highest lane still
// clears the top edge by EDGE_PX; `dieMotion` caps the arc the same way.
export function landingSpots(count: number, lane: number, { width, height, box = null, bottomInset = 0, scale = 1 }: ThrowArea) {
	const small = width < SM_PX;
	const px = (phone: number, desktop: number) => (small ? phone : desktop) * scale;
	const size = px(48, 60);
	const gap = px(58, 74);
	const edge = EDGE_PX * scale;
	const top = height - size - edge;
	const clear = Math.max(bottomInset, small && box ? height - box.top : 0);
	const floor0 = Math.max(Math.min(px(250, 96), top), clear ? clear + edge : 0);
	if (floor0 > top) return null;
	const step = Math.min(px(70, 100), (top - floor0) / (LANES - 1));
	const floor = floor0 + step * lane;
	const wanted = Math.max(px(70, 96), !small && box ? box.right + edge : 0);
	const left = Math.max(0, Math.min(wanted, width - (count - 1) * gap - size - 8 * scale));
	return { size, spots: Array.from({ length: count }, (_, n): Point => ({ x: left + gap * n, y: height - floor - size })) };
}

export function throwRoom(area: ThrowArea) {
	return landingSpots(1, 0, area) !== null;
}

type Part = "die" | "x" | "rise" | "shadow" | "arc" | "squash" | "spin" | "value";
export type Motion = { part: Part; keyframes: Keyframe[]; options: KeyframeEffectOptions };

// One die's flight from `from` (its offset at the start) to its spot, as the
// spec draws it: an arc up, a spin settling in the first two thirds, a squash
// at each touch, a shadow growing as it falls, and the value shown from the
// first touch. Transform and opacity only. It appears as it leaves and holds
// at full opacity once it lands — map-dice fades it later, together with its
// card. The arc's own peak is capped to the room above `spotY`, so a lane too
// close to the top (a short board) never sends the die past the edge; the
// smaller bounces shrink with it, keeping the same shape.
export function dieMotion(
	{ leaveAt, landAt }: ThrownDie,
	{ from, size, spotY, scale = 1 }: { from: Point; size: number; spotY: number; scale?: number },
): Motion[] {
	const [first, second] = THROW_TOUCHES;
	const flight = { duration: THROW_MS, delay: leaveAt, fill: "backwards" as const };
	const peak = Math.min(size * 2.5, Math.max(0, spotY - EDGE_PX * scale));
	const up = (ratio: number) => `translateY(${-Math.round(peak * ratio)}px)`;
	return [
		{
			part: "die",
			keyframes: [
				{ opacity: 0, offset: 0 },
				{ opacity: 0, offset: leaveAt / landAt },
				{ opacity: 1, offset: leaveAt / landAt },
				{ opacity: 1, offset: 1 },
			],
			options: { duration: landAt, fill: "forwards" },
		},
		{
			part: "x",
			keyframes: [{ transform: `translateX(${from.x}px)` }, { transform: "translateX(0px)" }],
			options: { ...flight, easing: "cubic-bezier(.25,.6,.4,1)" },
		},
		{
			part: "rise",
			keyframes: [
				{ transform: `translateY(${from.y}px)`, easing: "ease-out" },
				{ transform: "translateY(0px)", offset: first },
				{ transform: "translateY(0px)" },
			],
			options: flight,
		},
		{
			part: "arc",
			keyframes: [
				{ transform: "translateY(0px)", offset: 0, easing: "cubic-bezier(.2,.7,.4,1)" },
				{ transform: up(1), offset: 0.28, easing: "cubic-bezier(.6,0,.9,.5)" },
				{ transform: "translateY(0px)", offset: first, easing: "ease-out" },
				{ transform: up(0.47 / 2.5), offset: 0.76, easing: "ease-in" },
				{ transform: "translateY(0px)", offset: second, easing: "ease-out" },
				{ transform: up(0.08 / 2.5), offset: 0.95, easing: "ease-in" },
				{ transform: "translateY(0px)", offset: 1 },
			],
			options: flight,
		},
		{
			part: "squash",
			keyframes: [
				{ transform: "scale(1, 1)", offset: 0 },
				{ transform: "scale(1, 1)", offset: first - 0.04 },
				{ transform: "scale(1.2, 0.76)", offset: first + 0.01 },
				{ transform: "scale(0.94, 1.06)", offset: first + 0.08 },
				{ transform: "scale(1, 1)", offset: first + 0.14 },
				{ transform: "scale(1, 1)", offset: second - 0.02 },
				{ transform: "scale(1.08, 0.9)", offset: second + 0.01 },
				{ transform: "scale(1, 1)", offset: second + 0.06 },
				{ transform: "scale(1, 1)", offset: 1 },
			],
			options: flight,
		},
		{
			part: "spin",
			keyframes: [{ transform: `rotate(${-SPIN_TURNS_DEG}deg)` }, { transform: "rotate(0deg)" }],
			options: { duration: Math.round(THROW_MS * 0.66), delay: leaveAt, easing: "cubic-bezier(.3,.7,.4,1)", fill: "backwards" },
		},
		{
			part: "shadow",
			keyframes: [
				{ transform: "scale(0.4)", opacity: 0.1, offset: 0 },
				{ transform: "scale(0.3)", opacity: 0.05, offset: 0.28 },
				{ transform: "scale(1)", opacity: 0.35, offset: first },
				{ transform: "scale(0.75)", opacity: 0.2, offset: 0.76 },
				{ transform: "scale(1)", opacity: 0.3, offset: second },
				{ transform: "scale(1)", opacity: 0.3, offset: 1 },
			],
			options: flight,
		},
		{
			part: "value",
			keyframes: [
				{ opacity: 0, offset: 0 },
				{ opacity: 0, offset: first - 0.02 },
				{ opacity: 1, offset: first },
			],
			options: flight,
		},
	];
}

export type Thrown = {
	// Removes the dice at once: a skip, a withdrawn roll, a disconnect, a
	// reload, or a card dropped at the cap.
	clear(): void;
	// Fades the landed dice out over `ms`, then removes them — map-dice calls
	// this as its card fades. Expects the dice to have already landed; call
	// clear() instead for dice still mid-flight. A no-op once the dice are
	// already gone.
	fadeOut(ms: number): void;
	// Fades the landed dice out over `fadeMs` once `ms` have passed. A pointer resting on one of them holds them, and the
	// wait starts over once it leaves.
	linger(ms: number, fadeMs: number): void;
};

export type ThrowOptions = {
	faces: Face[];
	color: string | null;
	lane: number;
	// The roller's tab centre in the layer, or null for the bottom-left corner.
	origin: Point | null;
	box?: Box | null;
	bottomInset?: number;
	scale?: number;
	schedule: ThrowSchedule;
	// The element the roll plays on: its dice-landing events and its skip.
	target: HTMLElement;
	detail: { key: string; own: boolean };
	// The roll's verdict: a d20 showing 20 on a critical hit glows, one showing 1 on a critical miss greys.
	verdict?: string | null;
	// Called once, when the dice have landed — frees their lane, since a new
	// roll can throw over them in the same one.
	onLanded?: () => void;
	// Makes each die tappable from the moment it leaves, calling this; without it the dice are inert.
	onTap?: () => void;
	// Called once, when the dice have left the layer: cleared, or faded out.
	onGone?: () => void;
};

// Throws a roll's dice over the board: one flat die per face, up to the
// schedule's, in the roller's colour. Each moment dispatches on the target
// (dice-landing:throw, :bounce, :land) as throwMoments times it. Once landed
// the dice hold, showing their values, until fadeOut() or clear() takes them,
// or the target's skip clears them at once.
export function throwDice(
	layer: HTMLElement,
	{ faces, color, lane, origin, box, bottomInset, scale = 1, schedule, target, detail, verdict, onLanded, onTap, onGone }: ThrowOptions,
): Thrown {
	const timers: ReturnType<typeof setTimeout>[] = [];
	const animations: Animation[] = [];
	const dice: HTMLElement[] = [];
	let gone = false;
	let cleared = false;
	let landed = false;
	let hovered = false;
	let fading: Animation[] = [];
	let waiting: { timer: ReturnType<typeof setTimeout> | null; ms: number; fadeMs: number } | null = null;

	const land = () => {
		if (landed) return;
		landed = true;
		onLanded?.();
	};

	const clear = () => {
		if (cleared) return;
		cleared = true;
		gone = true;
		land();
		target.removeEventListener("dice-landing:skip", clear);
		for (const timer of timers) clearTimeout(timer);
		for (const animation of animations) animation.cancel();
		for (const die of dice) die.remove();
		onGone?.();
	};

	const fadeOut = (ms: number) => {
		if (gone) return;
		gone = true;
		land();
		target.removeEventListener("dice-landing:skip", clear);
		for (const timer of timers) clearTimeout(timer);
		if (!dice.length || typeof dice[0].animate !== "function") {
			clear();
			return;
		}
		fading = dice.map((die) => die.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms, fill: "forwards" }));
		animations.push(...fading);
		// A pointer that cancels the fade keeps the dice, so only a fade that finishes takes them.
		Promise.all(fading.map((fade) => fade.finished)).then(clear, () => {});
	};

	const wait = () => {
		if (!waiting || hovered || gone) return;
		const { ms, fadeMs } = waiting;
		waiting.timer = setTimeout(() => fadeOut(fadeMs), ms);
		timers.push(waiting.timer);
	};
	const linger = (ms: number, fadeMs: number) => {
		waiting = { timer: null, ms, fadeMs };
		wait();
	};
	const hold = () => {
		hovered = true;
		if (waiting?.timer) clearTimeout(waiting.timer);
		// The pointer reaching dice already fading brings them back whole.
		if (fading.length && waiting && !cleared) {
			for (const fade of fading) fade.cancel();
			fading = [];
			gone = false;
		}
	};
	const release = () => {
		if (!hovered) return;
		hovered = false;
		wait();
	};

	const thrown = faces.slice(0, schedule.dice.length);
	const landing =
		thrown.length && typeof layer.animate === "function"
			? landingSpots(thrown.length, lane, { width: layer.clientWidth, height: layer.clientHeight, box, bottomInset, scale })
			: null;
	if (!landing) {
		clear();
		return { clear, fadeOut, linger };
	}

	const { size, spots } = landing;
	const start = origin ?? { x: 33 * scale, y: layer.clientHeight - 33 * scale };
	const key = RING_COLORS[color] ? color : "carmesim";
	thrown.forEach(([sides, value], index) => {
		const spot = spots[index];
		const from = { x: Math.round(start.x - spot.x - size / 2), y: Math.round(start.y - spot.y - size / 2) };
		const fx: DieFx | null =
			sides === 20 && Math.abs(value) === 20 && verdict === "critical_hit"
				? "critico"
				: sides === 20 && Math.abs(value) === 1 && verdict === "critical_miss"
					? "erro"
					: null;
		const { die, parts } = buildDie({ sides, value, color: key, fx }, { size, spot });
		for (const { part, keyframes, options } of dieMotion(schedule.dice[index], { from, size, spotY: spot.y, scale }))
			animations.push(parts[part].animate(keyframes, options));
		if (onTap) {
			die.addEventListener("click", onTap);
			die.addEventListener("pointerenter", hold);
			die.addEventListener("pointerleave", release);
		}
		dice.push(die);
		layer.append(die);
	});

	for (const { at, name, index, bounce } of throwMoments(schedule)) {
		const [sides, value] = thrown[index];
		timers.push(
			setTimeout(() => {
				// A die waiting to leave is invisible over the roller, and must not take a tap meant for it.
				if (name === "throw" && onTap) dice[index]?.setAttribute("class", "odo-thrown-die odo-thrown-die--tap");
				target.dispatchEvent(new CustomEvent(`dice-landing:${name}`, { bubbles: true, detail: { ...detail, index, sides, value, ...(bounce && { bounce }) } }));
			}, at),
		);
	}
	timers.push(setTimeout(land, schedule.dice.at(-1)?.landAt ?? 0));
	target.addEventListener("dice-landing:skip", clear);
	return { clear, fadeOut, linger };
}

function node<T extends Element>(element: T, attributes: Record<string, string | number> = {}) {
	for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, String(value));
	return element;
}

const div = (className: string) => node(document.createElement("div"), { class: className });

function buildDie(look: DieSvgOptions, { size, spot }: { size: number; spot: Point }) {
	const die = div("odo-thrown-die");
	die.style.left = `${spot.x}px`;
	die.style.top = `${spot.y}px`;
	die.style.width = `${size}px`;
	die.style.height = `${size}px`;
	const x = div("odo-thrown-die__part");
	const rise = div("odo-thrown-die__part");
	const shadow = div("odo-thrown-die__shadow");
	const arc = div("odo-thrown-die__part");
	const squash = div("odo-thrown-die__part odo-thrown-die__squash");
	const spin = div("odo-thrown-die__part");

	const { svg, value } = buildDieSvg(look);
	spin.append(svg);
	squash.append(spin);
	arc.append(squash);
	rise.append(shadow, arc);
	x.append(rise);
	die.append(x);
	return { die, parts: { die, x, rise, shadow, arc, squash, spin, value } as Record<Part, Element> };
}
